import { useMemo } from 'react';
import type {
  RenderRow,
  RenderCell,
  CellStyle,
  ColumnFormat,
} from '@calculom/report-designer';
import { evaluateMock } from '@calculom/report-designer';

// ============================================================
// WebReportTable — renders compiled RenderRow[] as an HTML table
// ============================================================

interface WebReportTableProps {
  rows: RenderRow[];
}

export function WebReportTable({ rows }: WebReportTableProps) {
  // Evaluate all formula cells using the mock evaluator
  const evaluatedRows = useMemo(() => evaluateAllRows(rows), [rows]);

  if (evaluatedRows.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-400 italic">
        Add rows to the canvas to see a preview
      </div>
    );
  }

  // Get max column count for colSpan calculations
  const maxCols = getMaxColumnCount(evaluatedRows);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm font-mono">
        <tbody>
          {evaluatedRows.map((row, idx) => (
            <WebReportRow
              key={`${row.rowId}-${idx}`}
              row={row}
              maxCols={maxCols}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Row component
// ============================================================

interface WebReportRowProps {
  row: EvaluatedRow;
  maxCols: number;
}

function WebReportRow({ row, maxCols }: WebReportRowProps) {
  switch (row.kind) {
    case 'title_block':
      return <TitleBlockRow row={row} maxCols={maxCols} />;
    case 'column_headers':
      return <ColumnHeadersRow row={row} />;
    case 'header':
      return <HeaderRow row={row} maxCols={maxCols} />;
    case 'spacer':
      return <SpacerRow maxCols={maxCols} />;
    case 'line':
      return <LineRow maxCols={maxCols} />;
    case 'data':
    case 'subtotal':
    case 'calculated':
    case 'grand_total':
      return <DataRow row={row} />;
  }
}

// ============================================================
// Specialized row renderers
// ============================================================

function TitleBlockRow({ row, maxCols }: { row: EvaluatedRow; maxCols: number }) {
  const cell = row.cells[0];
  if (!cell) return null;
  const style = cellStyleToCSS(cell.renderCell.style);
  const text = cell.displayValue;

  return (
    <tr>
      <td colSpan={maxCols} style={style} className="px-2 py-1">
        {text}
      </td>
    </tr>
  );
}

function ColumnHeadersRow({ row }: { row: EvaluatedRow }) {
  return (
    <tr>
      {row.cells.map((cell, idx) => {
        const style = cellStyleToCSS(cell.renderCell.style);
        return (
          <td
            key={idx}
            style={style}
            className="px-2 py-1.5 font-bold text-xs uppercase tracking-wide"
          >
            {cell.displayValue}
          </td>
        );
      })}
    </tr>
  );
}

function HeaderRow({ row, maxCols }: { row: EvaluatedRow; maxCols: number }) {
  const cell = row.cells[0];
  if (!cell) return null;
  const style = cellStyleToCSS(cell.renderCell.style);

  return (
    <tr>
      <td colSpan={maxCols} style={style} className="px-2 py-1.5">
        {cell.displayValue}
      </td>
    </tr>
  );
}

function SpacerRow({ maxCols }: { maxCols: number }) {
  return (
    <tr>
      <td colSpan={maxCols} className="h-3" />
    </tr>
  );
}

function LineRow({ maxCols }: { maxCols: number }) {
  return (
    <tr>
      <td
        colSpan={maxCols}
        className="h-1"
        style={{ borderBottom: '1px solid #9ca3af' }}
      />
    </tr>
  );
}

function DataRow({ row }: { row: EvaluatedRow }) {
  const indentPx = row.depth * 16;

  return (
    <tr className={getRowClassName(row.kind)}>
      {row.cells.map((cell, idx) => {
        const isLabel = cell.renderCell.columnDef.type === 'label';
        const isAccountCode = cell.renderCell.columnDef.type === 'account_code';
        const style = cellStyleToCSS(cell.renderCell.style);
        const align = isLabel || isAccountCode ? 'left' : 'right';

        return (
          <td
            key={idx}
            style={{
              ...style,
              textAlign: align,
              paddingLeft: isLabel ? `${8 + indentPx}px` : '8px',
            }}
            className="px-2 py-0.5 whitespace-nowrap"
          >
            {isLabel || isAccountCode
              ? cell.displayValue
              : formatNumericValue(cell.numericValue, cell.renderCell.columnDef.format)}
          </td>
        );
      })}
    </tr>
  );
}

// ============================================================
// Evaluation: resolve FormulaInstructions to display values
// ============================================================

interface EvaluatedCell {
  renderCell: RenderCell;
  displayValue: string;
  numericValue: number | undefined;
}

interface EvaluatedRow {
  kind: RenderRow['kind'];
  rowId: string;
  nodeId: string;
  depth: number;
  cells: EvaluatedCell[];
}

/**
 * Evaluates all rows, resolving formulas to numeric values.
 * Processes rows in order so that sum_rows and row_arithmetic
 * can reference previously evaluated rows.
 */
function evaluateAllRows(rows: RenderRow[]): EvaluatedRow[] {
  const resolvedValues = new Map<string, number>();
  const result: EvaluatedRow[] = [];

  for (const row of rows) {
    const evaluatedCells: EvaluatedCell[] = [];
    // Track sibling values for variance calculations
    const siblingValues = new Map<string, number>();

    // First pass: evaluate non-variance cells
    for (const cell of row.cells) {
      if (cell.value.kind === 'text') {
        evaluatedCells.push({
          renderCell: cell,
          displayValue: cell.value.text,
          numericValue: undefined,
        });
      } else if (cell.value.kind === 'number') {
        evaluatedCells.push({
          renderCell: cell,
          displayValue: String(cell.value.value),
          numericValue: cell.value.value,
        });
        siblingValues.set(cell.columnDef.type, cell.value.value);
      } else if (cell.value.kind === 'formula') {
        const instruction = cell.value.instruction;
        if (instruction.type === 'variance_amount' || instruction.type === 'variance_percent') {
          // Defer variance — needs sibling values
          evaluatedCells.push({
            renderCell: cell,
            displayValue: '',
            numericValue: undefined,
          });
        } else {
          const val = evaluateMock(instruction, resolvedValues);
          evaluatedCells.push({
            renderCell: cell,
            displayValue: String(val),
            numericValue: val,
          });
          siblingValues.set(cell.columnDef.type, val);
        }
      } else {
        evaluatedCells.push({
          renderCell: cell,
          displayValue: '',
          numericValue: undefined,
        });
      }
    }

    // Second pass: resolve variance cells using sibling values
    for (let i = 0; i < evaluatedCells.length; i++) {
      const cell = row.cells[i]!;
      if (cell.value.kind === 'formula') {
        const instruction = cell.value.instruction;
        if (instruction.type === 'variance_amount' || instruction.type === 'variance_percent') {
          const val = evaluateMock(instruction, resolvedValues, cell.columnDef.type, siblingValues);
          evaluatedCells[i] = {
            renderCell: cell,
            displayValue: String(val),
            numericValue: val,
          };
        }
      }
    }

    // Store primary numeric value for this row (first non-label formula cell)
    const primaryCell = evaluatedCells.find(
      (c) => c.renderCell.columnDef.type !== 'label' && c.numericValue !== undefined,
    );
    if (primaryCell?.numericValue !== undefined) {
      resolvedValues.set(row.rowId, primaryCell.numericValue);
    }

    result.push({
      kind: row.kind,
      rowId: row.rowId,
      nodeId: row.nodeId,
      depth: row.depth,
      cells: evaluatedCells,
    });
  }

  return result;
}

// ============================================================
// Formatting helpers
// ============================================================

function formatNumericValue(
  value: number | undefined,
  format: ColumnFormat | undefined,
): string {
  if (value === undefined) return '';

  const decimals = format?.decimals ?? 2;
  const isNegative = value < 0;
  const absVal = Math.abs(value);

  if (format?.type === 'percent') {
    const pct = (value * 100).toFixed(decimals);
    if (isNegative && format.negativeFormat === 'parentheses') {
      return `(${Math.abs(value * 100).toFixed(decimals)}%)`;
    }
    return `${pct}%`;
  }

  const formatted = absVal.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  if (format?.type === 'currency') {
    if (isNegative) {
      if (format.negativeFormat === 'parentheses') {
        return `($${formatted})`;
      }
      return `-$${formatted}`;
    }
    return `$${formatted}`;
  }

  // Number or default
  if (isNegative) {
    if (format?.negativeFormat === 'parentheses') {
      return `(${formatted})`;
    }
    return `-${formatted}`;
  }
  return formatted;
}

function cellStyleToCSS(style: CellStyle | undefined): React.CSSProperties {
  if (!style) return {};

  const css: React.CSSProperties = {};

  if (style.bold) css.fontWeight = 'bold';
  if (style.italic) css.fontStyle = 'italic';
  if (style.fontSize) css.fontSize = `${style.fontSize}px`;
  if (style.alignment) css.textAlign = style.alignment;
  if (style.backgroundColor) css.backgroundColor = style.backgroundColor;
  if (style.textColor) css.color = style.textColor;
  if (style.borderBottom) {
    const width = style.borderBottom === 'thick' ? '3px'
      : style.borderBottom === 'medium' ? '2px'
      : style.borderBottom === 'double' ? '3px'
      : '1px';
    const lineStyle = style.borderBottom === 'double' ? 'double' : 'solid';
    css.borderBottom = `${width} ${lineStyle} #374151`;
  }

  return css;
}

function getRowClassName(kind: RenderRow['kind']): string {
  switch (kind) {
    case 'subtotal':
      return 'bg-gray-50';
    case 'grand_total':
      return '';
    case 'calculated':
      return '';
    default:
      return 'hover:bg-gray-50/50';
  }
}

function getMaxColumnCount(rows: EvaluatedRow[]): number {
  let max = 0;
  for (const row of rows) {
    if (row.cells.length > max) max = row.cells.length;
  }
  return max > 0 ? max : 1;
}
